import { isPolicyTaskId, isClientTaskId } from "shared/utils/client";
import type { ClientTaskId, PolicyTaskId, TaskId } from "shared/data/Tasks";
import type { ClientId, PolicyId } from "shared/types/Client";
import type { DocumentId } from "shared/types/Document";
import type {
  EIFPlanConfigEligibilityWithPFMLAndStatutorySubStep,
  EIFStepId,
  EIFSubStepId,
} from "shared/types/EIF";
import type { EmailId } from "shared/types/Email";
import type { EmployeeClassId } from "shared/types/EmployeeClass";
import type { ExplorerPageId, ExplorerPageVanitySlug } from "shared/types/ExplorerPage";
import type { OnboardingFormId } from "shared/types/OnboardingForm";
import type { RecipientId } from "shared/types/OnboardingFormRecipient";
import type { OutsideSignerActionId } from "shared/types/OutsideSigner";
import type { UserId } from "shared/types/User";

export const VIEW_TYPE = {
  internal: "internal",
  hub: "hub",
  broker: "broker",
  benAdmin: "benAdmin",
  basic: "basic",
  clear: "clear",
  auth: "auth",
  explorer: "explorer",
  outside_signer: "outside_signer",
} as const;

export const AUTH_TYPE = {
  required: "required", // user must be logged in
  not_allowed: "not_allowed", // user must not be logged in
  optional: "optional", // public route
  outside_signer: "outside_signer", // for outside signers only
} as const;

const isParamClientTaskId = (param: TaskId | ValidParam | undefined): param is TaskId => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return isClientTaskId(param as ClientTaskId);
};
const isParamPolicyTaskId = (param: TaskId | ValidParam | undefined): param is TaskId => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return isPolicyTaskId(param as PolicyTaskId);
};

export const RouteData = {
  index: {
    friendlyName: "Index",
    getPath: () => `/`,
    getPathTemplate: function () {
      return this.getPath();
    },
    viewType: VIEW_TYPE.basic,
    authType: AUTH_TYPE.required,
    domain: "onboard.com" as const,
  },
  notFound: {
    friendlyName: "404",
    getPath: () => `/404`,
    getPathTemplate: function () {
      return this.getPath();
    },
    viewType: VIEW_TYPE.basic,
    authType: AUTH_TYPE.optional,
    domain: "onboard.com" as const,
  },
  login: {
    friendlyName: "Login",
    getPath: () => `/login`,
    getPathTemplate: function () {
      return this.getPath();
    },
    viewType: VIEW_TYPE.auth,
    authType: AUTH_TYPE.not_allowed,
    domain: "onboard.com" as const,
  },
  maintenance: {
    friendlyName: "Maintenance",
    getPath: () => `/maintenance`,
    getPathTemplate: function () {
      return this.getPath();
    },
    viewType: VIEW_TYPE.basic,
    authType: AUTH_TYPE.optional,
    domain: "onboard.com" as const,
  },
  forgot: {
    friendlyName: "Forgot Password",
    getPath: () => `/forgot`,
    getPathTemplate: function () {
      return this.getPath();
    },
    viewType: VIEW_TYPE.auth,
    authType: AUTH_TYPE.not_allowed,
    domain: "onboard.com" as const,
  },
  activate: {
    friendlyName: "Account Activation",
    getPath: () => `/activate`,
    getPathTemplate: function () {
      return this.getPath();
    },
    viewType: VIEW_TYPE.auth,
    authType: AUTH_TYPE.not_allowed,
    domain: "onboard.com" as const,
  },
  reactivate: {
    friendlyName: "Resend Activation",
    getPath: () => `/reactivate`,
    getPathTemplate: function () {
      return this.getPath();
    },
    viewType: VIEW_TYPE.auth,
    authType: AUTH_TYPE.not_allowed,
    domain: "onboard.com" as const,
  },
  reset: {
    friendlyName: "Password Reset",
    getPath: () => `/reset`,
    getPathTemplate: function () {
      return this.getPath();
    },
    viewType: VIEW_TYPE.auth,
    authType: AUTH_TYPE.not_allowed,
    domain: "onboard.com" as const,
  },
  termsOfUse: {
    friendlyName: "Terms of Use",
    getPath: () => `/terms-of-use`,
    getPathTemplate: function () {
      return this.getPath();
    },
    viewType: VIEW_TYPE.basic,
    authType: AUTH_TYPE.required,
    domain: "onboard.com" as const,
  },
  homeHub: {
    friendlyName: "Home",
    getPath: (clientId: ClientId) => `/client/${clientId}`,
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.clientId);
    },
    viewType: VIEW_TYPE.hub,
    authType: AUTH_TYPE.required,
    getZendeskLabels: () => {
      return ["dashboard"];
    },
    domain: "onboard.com" as const,
  },
  welcome: {
    friendlyName: "Welcome",
    getPath: (clientId: ClientId) => `${RouteData.homeHub.getPath(clientId)}/welcome`,
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.clientId);
    },
    viewType: VIEW_TYPE.hub,
    authType: AUTH_TYPE.required,
    getZendeskLabels: () => {
      return ["welcome"];
    },
    domain: "onboard.com" as const,
  },
  setupPending: {
    friendlyName: "Setup Pending",
    getPath: (clientId: ClientId) => `${RouteData.homeHub.getPath(clientId)}/setup-pending`,
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.clientId);
    },
    viewType: VIEW_TYPE.basic,
    authType: AUTH_TYPE.required,
    getZendeskLabels: () => {
      return ["welcome"];
    },
    domain: "onboard.com" as const,
  },
  eifStepDetail: {
    friendlyName: "EIF Step Detail",
    getPath: (clientId: ClientId, eifStepId: EIFStepId | ValidParam) => {
      const home = RouteData.homeHub.getPath(clientId);
      return `${home}/task/eif-submit-company-information/step/${eifStepId}`;
    },
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.clientId, VALID_PARAMS.eifStepId);
    },
    viewType: VIEW_TYPE.hub,
    authType: AUTH_TYPE.required,
    getZendeskLabels: ({ eifStepId }: ValidParams) => {
      const record: Record<EIFStepId, string[]> = {
        "company-information": ["company details"],
        "plan-administrators-&-billing": ["plan admin"],
        "plan-configuration-&-eligibility": ["plan eligibility"],
        "other-contract-details": ["other contract details"],
        "review-&-submit": ["review submit"],
      };
      const labels = eifStepId ? record[eifStepId] : [];
      return labels;
    },
    domain: "onboard.com" as const,
  },
  stepPolicyDetail: {
    friendlyName: "Task Policy Detail",
    getPath: (clientId: ClientId, taskId: ClientTaskId | ValidParam, policyId: PolicyId) => {
      const home = RouteData.homeHub.getPath(clientId);
      return `${home}/task/${taskId}/policy/${policyId}`;
    },
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.clientId, VALID_PARAMS.taskId, VALID_PARAMS.policyId);
    },
    viewType: VIEW_TYPE.hub,
    authType: AUTH_TYPE.required,
    getZendeskLabels: () => {
      return ["enrollment event & connectivity"];
    },
    domain: "onboard.com" as const,
  },
  eifSubStepDetail: {
    friendlyName: "EIF Submit Company Info",
    getPath: (
      clientId: ClientId,
      eifStepId: EIFStepId | ValidParam,
      eifSubStepId: EIFSubStepId | ValidParam,
    ) =>
      `${RouteData.homeHub.getPath(
        clientId,
      )}/task/eif-submit-company-information/step/${eifStepId}/${eifSubStepId}`,
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.clientId, VALID_PARAMS.eifStepId, VALID_PARAMS.eifSubStepId);
    },
    viewType: VIEW_TYPE.hub,
    authType: AUTH_TYPE.required,
    getZendeskLabels: ({ eifStepId }: ValidParams) => {
      const record: Record<EIFStepId, string[]> = {
        "company-information": ["company details"],
        "plan-administrators-&-billing": ["plan admin"],
        "plan-configuration-&-eligibility": ["plan eligibility"],
        "other-contract-details": ["other contract details"],
        "review-&-submit": ["review submit"],
      };
      const labels = eifStepId ? record[eifStepId] : [];
      return labels;
    },
    domain: "onboard.com" as const,
  },
  eifSubStepPolicyDetail: {
    friendlyName: "EIF Sub Step Policy Detail",
    getPath: (
      clientId: ClientId,
      eifStepId: EIFStepId | ValidParam,
      eifSubStepId: EIFSubStepId | ValidParam,
      policyId: PolicyId,
    ) => {
      const home = RouteData.homeHub.getPath(clientId);
      return `${home}/task/eif-submit-company-information/step/${eifStepId}/${eifSubStepId}/policy/${policyId}`;
    },
    getPathTemplate: function () {
      return this.getPath(
        VALID_PARAMS.clientId,
        VALID_PARAMS.eifStepId,
        VALID_PARAMS.eifSubStepId,
        VALID_PARAMS.policyId,
      );
    },
    viewType: VIEW_TYPE.hub,
    authType: AUTH_TYPE.required,
    getZendeskLabels: ({ eifStepId }: ValidParams) => {
      const record: Record<EIFStepId, string[]> = {
        "company-information": ["company details"],
        "plan-administrators-&-billing": ["plan admin"],
        "plan-configuration-&-eligibility": ["plan eligibility"],
        "other-contract-details": ["other contract details"],
        "review-&-submit": ["review submit"],
      };
      const labels = eifStepId ? record[eifStepId] : [];
      return labels;
    },
    domain: "onboard.com" as const,
  },
  eifClassBuilderCreator: {
    friendlyName: "EIF Class Builder Creator",
    getPath: (
      clientId: ClientId,
      eifStepId: "plan-configuration-&-eligibility" | ValidParam,
      eifSubStepId: EIFPlanConfigEligibilityWithPFMLAndStatutorySubStep | ValidParam,
    ) => {
      const homeHubPath = RouteData.homeHub.getPath(clientId);
      type Path =
        `${string}/task/eif-submit-company-information/step/${typeof eifStepId}/${typeof eifSubStepId}/create`;
      const path: Path = `${homeHubPath}/task/eif-submit-company-information/step/${eifStepId}/${eifSubStepId}/create`;
      return path;
    },
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.clientId, VALID_PARAMS.eifStepId, VALID_PARAMS.eifSubStepId);
    },
    viewType: VIEW_TYPE.hub,
    authType: AUTH_TYPE.required,
    getZendeskLabels: () => {
      return ["plan eligibility"];
    },
    domain: "onboard.com" as const,
  },
  eifClassBuilderEdit: {
    friendlyName: "EIF Class Builder Edit",
    getPath: (
      clientId: ClientId,
      eifStepId: "plan-configuration-&-eligibility" | ValidParam,
      eifSubStepId: EIFPlanConfigEligibilityWithPFMLAndStatutorySubStep | ValidParam,
      employeeClassId: EmployeeClassId,
    ) => {
      const homeHubPath = RouteData.homeHub.getPath(clientId);
      type Path =
        `${string}/task/eif-submit-company-information/step/${typeof eifStepId}/${typeof eifSubStepId}/${string}`;
      const path: Path = `${homeHubPath}/task/eif-submit-company-information/step/${eifStepId}/${eifSubStepId}/${employeeClassId}`;
      return path;
    },
    getPathTemplate: function () {
      return this.getPath(
        VALID_PARAMS.clientId,
        VALID_PARAMS.eifStepId,
        VALID_PARAMS.eifSubStepId,
        VALID_PARAMS.employeeClassId,
      );
    },
    viewType: VIEW_TYPE.hub,
    authType: AUTH_TYPE.required,
    getZendeskLabels: () => {
      return ["plan eligibility"];
    },
    domain: "onboard.com" as const,
  },
  eifSummary: {
    friendlyName: "EIF Summary",
    getPath: (clientId: ClientId) =>
      `${RouteData.homeHub.getPath(clientId)}/task/eif-submit-company-information/summary`,
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.clientId);
    },
    viewType: VIEW_TYPE.hub,
    authType: AUTH_TYPE.required,
    getZendeskLabels: () => {
      return ["review submit"];
    },
    domain: "onboard.com" as const,
  },
  eifPushToQPS: {
    friendlyName: "Push to QPS",
    getPath: (clientId: ClientId) =>
      `${RouteData.homeHub.getPath(clientId)}/task/eif-submit-company-information/push-to-qps`,
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.clientId);
    },
    viewType: VIEW_TYPE.hub,
    authType: AUTH_TYPE.required,
    getZendeskLabels: () => {
      return [];
    },
    domain: "onboard.com" as const,
  },
  outsideSigner: {
    friendlyName: "Outside Signer",
    getPath: (clientId: ClientId, outsideSignerActionId: OutsideSignerActionId | ValidParam) =>
      `${RouteData.homeHub.getPath(clientId)}/outside-signer/${outsideSignerActionId}`,
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.clientId, VALID_PARAMS.outsideSignerActionId);
    },
    viewType: VIEW_TYPE.outside_signer,
    authType: AUTH_TYPE.outside_signer,
    getZendeskLabels: () => {
      return [];
    },
    domain: "onboard.com" as const,
  },
  outsideSignerEifSummary: {
    friendlyName: "Outside Signer - EIF Summary",
    getPath: (clientId: ClientId) =>
      `${RouteData.homeHub.getPath(clientId)}/outside-signer/eif-summary`,
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.clientId);
    },
    viewType: VIEW_TYPE.outside_signer,
    authType: AUTH_TYPE.outside_signer,
    getZendeskLabels: () => {
      return ["review submit"];
    },
    domain: "onboard.com" as const,
  },
  censusStepDetail: {
    friendlyName: "Census Upload and Validation",
    getPath: (clientId: ClientId, policyId: string) => {
      return `${RouteData.homeHub.getPath(
        clientId,
      )}/task/enrollment-elections/${policyId}/validations`;
    },
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.clientId, VALID_PARAMS.policyId);
    },
    viewType: VIEW_TYPE.hub,
    authType: AUTH_TYPE.required,
    getZendeskLabels: () => {
      return ["enrollment-elections-validation"];
    },
    domain: "onboard.com" as const,
  },
  censusValidateReportTab: {
    friendlyName: "Census Validation and Report tab",
    getPath: (clientId: ClientId, policyId: string, documentId: DocumentId) =>
      `${RouteData.homeHub.getPath(
        clientId,
      )}/task/enrollment-elections/${policyId}/validation-report/${documentId}`,
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.clientId, VALID_PARAMS.policyId, VALID_PARAMS.documentId);
    },
    viewType: VIEW_TYPE.clear,
    authType: AUTH_TYPE.required,
    domain: "onboard.com" as const,
  },
  clientTaskDetail: {
    friendlyName: "Task Detail",
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    getPath: ((clientId: ClientId, taskId: ClientTaskId): string =>
      `${RouteData.homeHub.getPath(clientId)}/task/${taskId}`) as {
      (clientId: ClientId, taskId: ClientTaskId | ValidParam): string;
    },
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.clientId, VALID_PARAMS.taskId);
    },
    viewType: VIEW_TYPE.hub,
    authType: AUTH_TYPE.required,
    getZendeskLabels: ({ taskId }: ValidParams) => {
      if (isParamClientTaskId(taskId)) {
        const r: Record<ClientTaskId, string[]> = {
          "kickoff-call": ["kickoff call"],
          "ongoing-service-call": ["ongoing service call"],
          "onboarding-forms": ["onboarding forms"],
          "prior-carrier-certificates": ["prior carrier certificates"],
          "sun-life-connect": ["sun life connect"],
          "enrollment-resources": ["enrollment resources"],
          "enrollment-event-and-connectivity": ["enrollment event and connectivity"],
          "eif-submit-company-information": ["digital smart form"],
        };
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const labels = r[taskId as ClientTaskId];
        return labels;
      }
      return [];
    },
    domain: "onboard.com" as const,
  },
  policyTaskDetail: {
    friendlyName: "PolicyTask Detail",
    getPath: (clientId: ClientId, taskId: PolicyTaskId | ValidParam, policyId: PolicyId): string =>
      `${RouteData.homeHub.getPath(clientId)}/task/${taskId}/${policyId}`,
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.clientId, VALID_PARAMS.taskId, VALID_PARAMS.policyId);
    },
    viewType: VIEW_TYPE.hub,
    authType: AUTH_TYPE.required,
    getZendeskLabels: ({ policyTaskId }: ValidParams) => {
      if (isParamPolicyTaskId(policyTaskId)) {
        const r: Record<PolicyTaskId, string[]> = {
          "prior-carrier-bills": ["prior carrier bills"],
          "enrollment-elections": ["enrollment elections"],
          pfml: ["pfml"],
          "pfml-spd": ["pfml-spd"],
          "sunadvisor-spd": ["sunadvisor-spd"],
          "dental-rollover": [],
          "deductible-maximum-report": [],
        };
        const labels = r[policyTaskId];
        return labels;
      }
      return [];
    },
    domain: "onboard.com" as const,
  },
  resourcesListing: {
    friendlyName: "Resources Listing",
    getPath: (clientId: ClientId) => `${RouteData.homeHub.getPath(clientId)}/resources-listing`,
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.clientId);
    },
    viewType: VIEW_TYPE.hub,
    authType: AUTH_TYPE.required,
    getZendeskLabels: () => {
      return ["resources listing"];
    },
    domain: "onboard.com" as const,
  },
  homeInternal: {
    friendlyName: "Clients",
    getPath: () => `/clients/admin`,
    getPathTemplate: function () {
      return this.getPath();
    },
    viewType: VIEW_TYPE.internal,
    authType: AUTH_TYPE.required,
    domain: "onboard.com" as const,
  },
  clientDetail: {
    friendlyName: "Client Details",
    getPath: (clientId: ClientId) => `${RouteData.homeInternal.getPath()}/${clientId}/manage`,
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.clientId);
    },
    viewType: VIEW_TYPE.internal,
    authType: AUTH_TYPE.required,
    domain: "onboard.com" as const,
  },
  usersInternal: {
    friendlyName: "Internal Users",
    getPath: () => `/users/internal`,
    getPathTemplate: function () {
      return this.getPath();
    },
    viewType: VIEW_TYPE.internal,
    authType: AUTH_TYPE.required,
    domain: "onboard.com" as const,
  },
  brokers: {
    friendlyName: "Brokers",
    getPath: () => `/brokers/admin/`,
    getPathTemplate: function () {
      return this.getPath();
    },
    viewType: VIEW_TYPE.internal,
    authType: AUTH_TYPE.required,
    domain: "onboard.com" as const,
  },
  brokerDetail: {
    friendlyName: "Broker Details",
    getPath: (userId: UserId) => `${RouteData.brokers.getPath()}${userId}/manage`,
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.userId);
    },
    viewType: VIEW_TYPE.internal,
    authType: AUTH_TYPE.required,
    domain: "onboard.com" as const,
  },
  benAdmins: {
    friendlyName: "Users",
    getPath: () => `/ben-admins/admin/`,
    getPathTemplate: function () {
      return this.getPath();
    },
    viewType: VIEW_TYPE.internal,
    authType: AUTH_TYPE.required,
    domain: "onboard.com" as const,
  },
  benAdminDetail: {
    friendlyName: "User Details",
    getPath: (userId: UserId) => `${RouteData.benAdmins.getPath()}${userId}/manage`,
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.userId);
    },
    viewType: VIEW_TYPE.internal,
    authType: AUTH_TYPE.required,
    domain: "onboard.com" as const,
  },
  onboardingFormsSigned: {
    friendlyName: "Benefits Documents - Signed",
    getPath: (clientId: ClientId, onboardingFormId: OnboardingFormId, recipientId: RecipientId) =>
      `${RouteData.homeHub.getPath(
        clientId,
      )}/onboarding-forms/${onboardingFormId}/action/${recipientId}`,
    getPathTemplate: function () {
      return this.getPath(
        VALID_PARAMS.clientId,
        VALID_PARAMS.onboardingFormId,
        VALID_PARAMS.recipientId,
      );
    },
    viewType: VIEW_TYPE.hub,
    authType: AUTH_TYPE.required,
    domain: "onboard.com" as const,
  },
  onboardingFormsViewer: {
    friendlyName: "Benefits Documents - Signed",
    getPath: (clientId: ClientId, onboardingFormId: OnboardingFormId, recipientId: RecipientId) =>
      `${RouteData.homeHub.getPath(
        clientId,
      )}/onboarding-forms/${onboardingFormId}/view/${recipientId}`,
    getPathTemplate: function () {
      return this.getPath(
        VALID_PARAMS.clientId,
        VALID_PARAMS.onboardingFormId,
        VALID_PARAMS.recipientId,
      );
    },
    viewType: VIEW_TYPE.hub,
    authType: AUTH_TYPE.required,
    domain: "onboard.com" as const,
  },
  allEmails: {
    friendlyName: "Emails",
    getPath: () => {
      return `/emails`;
    },
    getPathTemplate: function () {
      return this.getPath();
    },
    viewType: VIEW_TYPE.internal,
    authType: AUTH_TYPE.required,
    domain: "onboard.com" as const,
  },
  emails: {
    friendlyName: "Emails",
    getPath: (clientId: ClientId) => {
      return `${RouteData.homeHub.getPath(clientId)}/emails`;
    },
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.clientId);
    },
    viewType: VIEW_TYPE.hub,
    authType: AUTH_TYPE.required,
    domain: "onboard.com" as const,
  },
  email: {
    friendlyName: "Email",
    getPath: (emailId: EmailId) => {
      return `/emails/${emailId}`;
    },
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.emailId);
    },
    viewType: VIEW_TYPE.internal,
    authType: AUTH_TYPE.required,
    domain: "onboard.com" as const,
  },
  emailClient: {
    friendlyName: "Email",
    getPath: (clientId: ClientId, emailId: EmailId) => {
      return `${RouteData.homeHub.getPath(clientId)}/emails/${emailId}`;
    },
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.clientId, VALID_PARAMS.emailId);
    },
    viewType: VIEW_TYPE.hub,
    authType: AUTH_TYPE.required,
    domain: "onboard.com" as const,
  },
  homeBroker: {
    friendlyName: "Home",
    getPath: () => `/clients`,
    getPathTemplate: function () {
      return this.getPath();
    },
    viewType: VIEW_TYPE.broker,
    authType: AUTH_TYPE.required,
    getZendeskLabels: () => {
      return ["clients"];
    },
    domain: "onboard.com" as const,
  },
  homeBenAdmin: {
    friendlyName: "Home",
    getPath: () => `/clients/ben-admin`,
    getPathTemplate: function () {
      return this.getPath();
    },
    viewType: VIEW_TYPE.benAdmin,
    authType: AUTH_TYPE.required,
    getZendeskLabels: () => {
      return ["clients"];
    },
    domain: "onboard.com" as const,
  },
  createBenefitsExplorerSite: {
    friendlyName: "Create Benefits Explorer page",
    getPath: (clientId: ClientId) =>
      `${RouteData.homeInternal.getPath()}/${clientId}/manage/benefits-explorer/`,
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.clientId);
    },
    viewType: VIEW_TYPE.internal,
    authType: AUTH_TYPE.required,
    domain: "onboard.com" as const,
  },
  editBenefitsExplorerSite: {
    friendlyName: "Edit Benefits Explorer page",
    getPath: (clientId: ClientId, explorerPageId: ExplorerPageId) =>
      `${RouteData.homeInternal.getPath()}/${clientId}/manage/benefits-explorer/${explorerPageId}`,
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.clientId, VALID_PARAMS.explorerPageId);
    },
    viewType: VIEW_TYPE.internal,
    authType: AUTH_TYPE.required,
    domain: "onboard.com" as const,
  },
  renewBenefitsExplorerSite: {
    friendlyName: "Renew Benefits Explorer page",
    getPath: (clientId: ClientId, explorerPageId: ExplorerPageId) =>
      `${RouteData.homeInternal.getPath()}/${clientId}/manage/benefits-explorer/${explorerPageId}/renew`,
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.clientId, VALID_PARAMS.explorerPageId);
    },
    viewType: VIEW_TYPE.internal,
    authType: AUTH_TYPE.required,
    domain: "onboard.com" as const,
  },
  benefitsExplorerIndexInternal: {
    friendlyName: "Benefits Explorer",
    getPath: () => `/benefits-explorer`,
    getPathTemplate: function () {
      return this.getPath();
    },
    viewType: VIEW_TYPE.explorer,
    authType: AUTH_TYPE.optional,
    domain: "onboard.com" as const,
  },
  benefitsExplorerInternal: {
    friendlyName: "Benefits Explorer",
    getPath: (explorerPageId: ExplorerPageId | ExplorerPageVanitySlug) =>
      `/benefits-explorer/${explorerPageId}`,
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.explorerPageId);
    },
    viewType: VIEW_TYPE.explorer,
    authType: AUTH_TYPE.optional,
    domain: "onboard.com" as const,
  },
  personalBenefitsPlanInternal: {
    friendlyName: "Personal Benefits Plan",
    getPath: (explorerPageId: ExplorerPageId | ExplorerPageVanitySlug) =>
      `/benefits-explorer/${explorerPageId}/personal-benefits-plan`,
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.explorerPageId);
    },
    viewType: VIEW_TYPE.explorer,
    authType: AUTH_TYPE.optional,
    domain: "onboard.com" as const,
  },

  // yourbenefisexplorer.com Ben Ex routes
  benefitsExplorerIndex: {
    friendlyName: "Benefits Explorer",
    getPath: () => `/`,
    getPathTemplate: function () {
      return this.getPath();
    },
    viewType: VIEW_TYPE.explorer,
    authType: AUTH_TYPE.optional,
    domain: "yourbenefitsexplorer.com" as const,
  },
  benefitsExplorer: {
    friendlyName: "Benefits Explorer",
    getPath: (explorerPageId: ExplorerPageId | ExplorerPageVanitySlug) => `/${explorerPageId}`,
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.explorerPageId);
    },
    viewType: VIEW_TYPE.explorer,
    authType: AUTH_TYPE.optional,
    domain: "yourbenefitsexplorer.com" as const,
  },
  personalBenefitsPlan: {
    friendlyName: "Personal Benefits Plan",
    getPath: (explorerPageId: ExplorerPageId | ExplorerPageVanitySlug) =>
      `/${explorerPageId}/personal-benefits-plan`,
    getPathTemplate: function () {
      return this.getPath(VALID_PARAMS.explorerPageId);
    },
    viewType: VIEW_TYPE.explorer,
    authType: AUTH_TYPE.optional,
    domain: "yourbenefitsexplorer.com" as const,
  },
};

const VALID_PARAMS = {
  userId: `:userId`,
  taskId: `:taskId`,
  clientId: `:clientId`,
  policyTaskId: `:policyTaskId`,
  onboardingFormId: `:onboardingFormId`,
  recipientId: `:recipientId`,
  explorerPageId: `:explorerPageId`,
  policyId: `:policyId`,
  eifStepId: ":eifStepId",
  eifSubStepId: ":eifSubStepId",
  employeeClassId: ":employeeClassId",
  emailId: ":emailId",
  documentId: ":documentId",
  outsideSignerActionId: ":outsideSignerActionId",
} as const;

export type ParamToType = {
  userId: UserId;
  taskId: TaskId;
  policyTaskId: PolicyTaskId;
  clientId: ClientId;
  onboardingFormId: OnboardingFormId;
  recipientId: RecipientId;
  explorerPageId: ExplorerPageId;
  eifStepId: EIFStepId;
  eifSubStepId: EIFSubStepId;
  employeeClassId: EmployeeClassId;
  policyId: string;
  emailId: EmailId;
  documentId: DocumentId;
  outsideSignerActionId: OutsideSignerActionId;
};

export type ValidParams = {
  [K in keyof typeof VALID_PARAMS]?: ParamToType[K];
};

type ValueOf<T> = T[keyof T];
export type ValidParam = ValueOf<typeof VALID_PARAMS>;
export type ValidRoute = keyof typeof RouteData;

export const UNSCHEDULED_MAINTENANCE_MESSAGE = "Unscheduled maintenance";
export const SCHEDULED_MAINTENANCE_MESSAGE = "Scheduled maintenance";
